/**
 * Local record of claim submissions made from this browser.
 *
 * The claim verification email contains only a token — no email or slug — so
 * when a verification link expires (410) the verify page needs this map to
 * offer a one-click "send a new verification email" recovery. The embedded
 * claim form upserts an entry on every successful submission.
 */
export interface ClaimContext {
  expert_slug: string;
  email: string;
  expert_name: string;
  submitted_at: string;
}

const STORAGE_KEY = 'se-claim-contexts';

export function readClaimContexts(): Record<string, ClaimContext> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const contexts: Record<string, ClaimContext> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      const candidate = value as Partial<ClaimContext>;
      if (typeof candidate.expert_slug !== 'string' || !candidate.expert_slug) continue;
      if (typeof candidate.email !== 'string' || !candidate.email) continue;

      contexts[key] = {
        expert_slug: candidate.expert_slug,
        email: candidate.email,
        expert_name: typeof candidate.expert_name === 'string' && candidate.expert_name
          ? candidate.expert_name
          : candidate.expert_slug,
        submitted_at: typeof candidate.submitted_at === 'string' ? candidate.submitted_at : '',
      };
    }
    return contexts;
  } catch {
    return {};
  }
}

export function upsertClaimContext(context: Omit<ClaimContext, 'submitted_at'>): void {
  try {
    const contexts = readClaimContexts();
    const key = `${context.expert_slug}:${context.email.toLowerCase()}`;
    contexts[key] = { ...context, submitted_at: new Date().toISOString() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(contexts));
  } catch {
    // localStorage unavailable (private mode, disabled storage) — recovery
    // affordances degrade gracefully, so this is non-fatal.
  }
}

/** Contexts sorted newest-first for recovery pickers. */
export function listClaimContexts(): ClaimContext[] {
  return Object.values(readClaimContexts()).sort((a, b) => b.submitted_at.localeCompare(a.submitted_at));
}
